/*@flow*/
import assert from './assert';
import isTypeName from './isTypeName';
import isFunction from './isFunction';
import isMaybe from './isMaybe';
import isIdentity from './isIdentity';
import Any from './Any';
import create from './create';
import Nil from './Nil';
import forbidNewOperator from './forbidNewOperator';
import is from './is';
import getTypeName from './getTypeName';
import type { TypeT, Predicate } from './types';
import withOptions from './withOptions';

type MaybeType =
  | TypeT<{ type: string }>
  | TypeT<{ type: TypeT<*> }>
  | TypeT<{ types: TypeT<*>[] }>
  | TypeT<{ identity: true, map: { [key: string]: string } }>
  | TypeT<{ type: TypeT<*>, options: { [key: string]: any } }>
  | TypeT<{ props: { [key: string]: any }, options: { [key: string]: any } }>
  | TypeT<{ type: TypeT<*>, predicate: Predicate }>;

function getDefaultName(type: TypeT<*>): string {
  return '?' + getTypeName(type);
}

function maybe(type: MaybeType, name?: string): MaybeType {
  if (isMaybe(type) || type === Any || type === Nil) {
    // makes the combinator idempotent and handle Any, Nil
    return type;
  }

  if (process.env.NODE_ENV !== 'production') {
    assert(isFunction(type), function() {
      return (
        'Invalid argument type ' +
        assert.stringify(type) +
        ' supplied to maybe(type, [name]) combinator (expected a type)'
      );
    });
    assert(isTypeName(name), function() {
      return (
        'Invalid argument name ' +
        assert.stringify(name) +
        ' supplied to maybe(type, [name]) combinator (expected a string)'
      );
    });
  }

  let displayName = name || getDefaultName(type);
  let identity = isIdentity(type);

  function Maybe(value: any, path?: string[]) {
    if (process.env.NODE_ENV !== 'production') {
      if (identity) {
        forbidNewOperator(this, Maybe);
      }
    }
    return Nil.is(value) ? value : create(type, value, path);
  }

  Maybe.meta = {
    kind: 'maybe',
    type,
    name,
    identity,
  };

  Maybe.displayName = displayName;

  Maybe.is = (x) => Nil.is(x) || is(x, type);

  withOptions(Maybe);

  return Maybe;
}

maybe.getDefaultName = getDefaultName;
export default maybe;
